import React, { useEffect, useMemo, useRef } from 'react'
import { getSessionId } from '@fs/zion-session'
import { useHistory } from '@fs/zion-router'
import { useUserProfile, useUser } from '@fs/zion-user'
import { Button, H1 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { ArrowCaret } from '@fs/zion-icon'
import KioskDrawer from './KioskDrawer'
import useKioskIdle from '../../hooks/useKioskIdle'
import { getKioskConfig, undockKiosk } from '../../helpers/kioskConfig'
import { useLogout } from '../../hooks/discoveryCenter/centerHooks'
import { useDSCState } from '../../../state/DSCStore'
import { getUserEmail } from '../../helpers/utility'
import apps from '../../experiences/kioskApps'

export default function StationMenu({ activeExperienceId }) {
  const menuRef = useRef(null)
  const history = useHistory()
  const { t } = useTranslation()
  const [logout, logoutState] = useLogout()
  const user = useUser()
  const { userProfile } = useUserProfile(user?.cisId)
  const dscState = useDSCState()
  const fullPortraitUrl = userProfile?.portrait?.links?.thumb64Uri
    ? `${userProfile?.portrait?.links?.thumb64Uri}?sessionId=${getSessionId(true)}`
    : ''
  const gender = user?.gender?.toLowerCase()
  const email = getUserEmail(dscState, user)

  const upperCSS = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `

  const titleCSS = css`
    inset-inline-start: 0px;
    position: absolute;
    z-index: -1;
    width: 100vw;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  const warnMissingConfig = () => {
    // eslint-disable-next-line no-alert
    alert('Unable to find session configuration information!')
  }
  const initiateLogout = () => {
    logout()
  }

  const conf = useMemo(() => getKioskConfig(), [])

  if (!conf) {
    warnMissingConfig()
  }

  useEffect(() => {
    if (logoutState.complete) {
      undockKiosk({ resetContainerSession: true })
    }
  }, [logoutState.complete])

  useEffect(() => {
    if (menuRef.current) {
      document.documentElement.style.setProperty('--station-menu-height', `${menuRef.current.offsetHeight}px`)
    }
    return () => {
      document.documentElement.style.removeProperty('--station-menu-height')
    }
  }, [])

  useKioskIdle(conf?.timeOut ?? 600, () => {
    initiateLogout()
  })

  const leaveKiosk = () => {
    undockKiosk()
  }

  const handleMenuClick = () => {
    history.push('/selectexperience')
  }

  const app = apps[activeExperienceId.toLowerCase()]
  const backButtonText = conf?.navMenu ? t('common-ui:menu.label', 'Menu') : t('kiosk-menu.leave-kiosk', 'Leave Kiosk')

  return (
    <div
      ref={menuRef}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        width: '100%',
        background: '#FFF',
        paddingInlineStart: '20px',
        paddingInlineEnd: '20px',
        borderBottom: '2px solid #DDDFDF',
      }}
    >
      <div css={upperCSS}>
        <Button
          data-testid="station-menu-leave-kiosk-button"
          emphasis="low"
          Icon={ArrowCaret}
          iconDirection="backward"
          onClick={conf?.navMenu ? handleMenuClick : leaveKiosk}
          linkName="StationMenu:LEAVE_KIOSK:CLICK"
        >
          {backButtonText}
        </Button>
        <div css={titleCSS}>
          <H1 size="H5" centered>
            {t(app?.title)}
          </H1>
        </div>

        <KioskDrawer
          logoutCallback={initiateLogout}
          email={email}
          userDisplayName={dscState?.nonstandardUser?.displayName}
          userPortrait={fullPortraitUrl}
          userGender={gender}
        />
      </div>
    </div>
  )
}

StationMenu.propTypes = {}
